import {
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import type { ReactElement } from "react";

import { compactControlHeight } from "../../theme/compact.js";

/**
 * `<DataPageFooter>` — canonical list-page footer.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 4".
 *
 * SIZE (2026-09-23, Christian: "the pagination elements are sometimes the
 * biggest font size in the entire modal"). They were: the count and the label
 * rendered at `body2`, and the page-size Select inherited the theme's
 * COMFORTABLE 44px control target — so a list page's densest content sat
 * under its largest chrome. Everything here is now the dense band UIOPT-01
 * already uses for control rows: 13px text, a 32px select, a small pager.
 * This is the shared footer, so the change lands on every data page in every
 * module at once — which is the point of it being a template.
 *
 * - LEFT — total count text. Singular vs plural derived from `total`:
 *   `"1 <singular>"` when `total === 1`, else `"<N> <entityLabel>"`.
 *   Default singular is the plural with a trailing `s` trimmed; callers
 *   that need a different singular form pass `entityLabelSingular`
 *   explicitly (e.g. `"data acquisition"` for plural
 *   `"data acquisitions"`).
 * - RIGHT — `Rows per page` Select + MUI Pagination.
 * - Always rendered (even on empty states) so the geometric assertion
 *   has a stable target.
 */
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export interface DataPageFooterProps {
  total: number;
  /** Plural noun, lower-case (e.g. `"sites"`, `"turbine models"`). */
  entityLabel: string;
  /** Optional override for the singular form. Defaults to `entityLabel`
   *  with a trailing `s` trimmed (`"sites" → "site"`). */
  entityLabelSingular?: string;
  page: number;
  pageSize: number;
  /** Page-size combobox options. Defaults to `[25, 50, 100]`. */
  pageSizeOptions?: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Test-id on the total-text `<Typography>`. Load-bearing — the
   *  acceptance suites pin assertions to this id. */
  totalTestId?: string;
  /** Narrow columns (a dialog's side table) cannot fit the full footer: the
   *  "Rows per page" label wraps the pager onto a second line. Compact drops
   *  the label (the select keeps its aria-label), shrinks the pager and
   *  shortens the count to "38 logs". */
  compact?: boolean;
}

export function DataPageFooter({
  total,
  entityLabel,
  entityLabelSingular,
  page,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
  totalTestId,
  compact = false,
}: DataPageFooterProps): ReactElement {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamp the visible page index against the (possibly) shrunk total so
  // Pagination's selected page stays in range when filters narrow the
  // result set under the current cursor.
  const visiblePage = Math.min(page, totalPages);
  const singular =
    entityLabelSingular ??
    (entityLabel.endsWith("s") ? entityLabel.slice(0, -1) : entityLabel);
  const countText =
    total === 1
      ? `1 ${singular}${compact ? "" : " in total"}`
      : `${String(total)} ${entityLabel}${compact ? "" : " in total"}`;
  const handlePageSize = (e: SelectChangeEvent<number>): void => {
    onPageSizeChange(Number(e.target.value));
  };

  // Stable id for the page-size label. Derived from the testid prefix
  // (when provided) so two footers on the same page don't collide. Falls
  // back to a constant when no testid is given.
  const pageSizeLabelId = totalTestId
    ? `${totalTestId}-page-size-label`
    : "data-page-footer-page-size-label";

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={(t) => ({
        flexShrink: 0,
        py: compact ? 0.75 : 1.5,
        gap: compact ? 1 : 2,
        flexWrap: "nowrap",
        borderTop: `1px solid ${t.palette.divider}`,
      })}
    >
      <Typography
        {...(totalTestId !== undefined ? { "data-testid": totalTestId } : {})}
        color="text.secondary"
        sx={{ fontSize: 13 }}
      >
        {countText}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={compact ? 1 : 2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {compact ? null : (
            <Typography
              color="text.secondary"
              component="label"
              id={pageSizeLabelId}
              sx={{ fontSize: 13 }}
            >
              Rows per page
            </Typography>
          )}
          <Select
            size="small"
            value={pageSize}
            onChange={handlePageSize}
            inputProps={{
              "aria-label": "Rows per page",
              ...(compact ? {} : { "aria-labelledby": pageSizeLabelId }),
            }}
            MenuProps={{
              // The menu is a portal and cannot inherit the field's density.
              PaperProps: {
                sx: { "& .MuiMenuItem-root": { fontSize: 13, minHeight: 32 } },
              },
            }}
            sx={{
              minWidth: compact ? 64 : 80,
              // The theme gives a Select the COMFORTABLE 44px target; a list
              // footer is a dense control row, not a touch surface.
              "& .MuiInputBase-input": { fontSize: 13, py: 0.5 },
              "& .MuiSelect-select": { minHeight: "unset" },
              minHeight: compactControlHeight,
              height: compactControlHeight,
            }}
          >
            {pageSizeOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Pagination
          page={visiblePage}
          count={totalPages}
          onChange={(_e, p) => {
            onPageChange(p);
          }}
          color="primary"
          aria-label="Pagination"
          // `small` in BOTH modes now: the default pager's 32px items with a
          // 14px label were the largest type on several pages.
          size="small"
          {...(compact ? { siblingCount: 0, boundaryCount: 1 } : {})}
          sx={{
            "& .MuiPaginationItem-root": {
              fontSize: 13,
              minWidth: 28,
              height: 28,
              margin: "0 1px",
            },
          }}
        />
      </Stack>
    </Stack>
  );
}
